import { useMemo } from 'react';
import { filterObject } from '@src/types/Archivist';
import {
  FilterField,
  FilterState,
  FilterOptions,
  FilterLoadingState,
} from '../types';
import { buildFilterArray } from '../utils';
import {
  useDataPointSearchState,
  useDataPointSearchActions,
} from '../context/DataPointSearchContext';
import { useMultiFieldDistinct } from './useMultiFieldDistinct';

interface UseCascadingFiltersReturn {
  filters: FilterState;
  options: FilterOptions;
  loading: FilterLoadingState;
  activeFilters: filterObject[];
  handleFilterChange: (field: FilterField, value: string) => void;
  clearAllFilters: () => void;
  isFieldEnabled: (field: FilterField) => boolean;
}

export const useCascadingFilters = (): UseCascadingFiltersReturn => {
  const state = useDataPointSearchState();
  const { setFilter, clearFilters } = useDataPointSearchActions();

  const activeFilters = useMemo(
    () => buildFilterArray(state.filters),
    [state.filters],
  );

  const { options, isLoading } = useMultiFieldDistinct(state.filters);

  const loading: FilterLoadingState = useMemo(
    () => ({
      deviceType: isLoading,
      parentDeviceId: isLoading,
      deviceId: isLoading,
      pointName: isLoading,
    }),
    [isLoading],
  );

  const isFieldEnabled = (field: FilterField): boolean => {
    if (field === FilterField.DEVICE_TYPE) {
      return true;
    }
    return !!state.filters.deviceType;
  };

  const handleFilterChange = (field: FilterField, value: string) => {
    setFilter(field, value);
  };

  return {
    filters: state.filters,
    options,
    loading,
    activeFilters,
    handleFilterChange,
    clearAllFilters: clearFilters,
    isFieldEnabled,
  };
};

export default useCascadingFilters;
